import { Extension } from '@tiptap/core'
import { Node as ProseMirrorNode } from '@tiptap/pm/model'
import { Plugin, PluginKey } from '@tiptap/pm/state'
import { IS_NON_MANUAL_TEXT_UPDATE } from '../../constants'

export interface WordCountLimitOptions {
  limit: number | null | undefined
}

export interface WordCountLimitStorage {
  /**
   * Get the number of words for the current document.
   * @param options The options for the character count. (optional)
   * @param options.node The node to get the words from. Defaults to the current document.
   */
  words: (options?: { node?: ProseMirrorNode }) => number
}

/**
 * This extension allows you to limit the words of your document.
 * created with reference from https://github.com/ueberdosis/tiptap/blob/main/packages/extension-character-count/src/character-count.ts
 */
export const WordCountLimit = Extension.create<
  WordCountLimitOptions,
  WordCountLimitStorage
>({
  name: 'wordCountLimit',

  addOptions() {
    return {
      limit: null,
    }
  },

  addStorage() {
    return {
      words: () => 0,
    }
  },

  onBeforeCreate() {
    this.storage.words = (options) => {
      const node = options?.node || this.editor.state.doc
      const text = node.textBetween(0, node.content.size, ' ', ' ')
      const words = text.split(/\s+/g)

      return words.length
    }
  },

  addProseMirrorPlugins() {
    return [
      new Plugin({
        key: new PluginKey('wordCountLimit'),
        filterTransaction: (transaction, state) => {
          const { limit } = this.options
          const isManualUpdate = transaction.getMeta(IS_NON_MANUAL_TEXT_UPDATE)
          // Nothing has changed or no limit is defined or isManualUpdate event. Ignore it.
          if (
            !transaction.docChanged ||
            limit === 0 ||
            limit === null ||
            limit === undefined ||
            isManualUpdate
          ) {
            return true
          }

          const oldSize = this.storage.words({ node: state.doc })
          const newSize = this.storage.words({ node: transaction.doc })

          // Everything is in the limit. Good.
          if (newSize <= limit) {
            return true
          }

          // The limit has already been exceeded but will be reduced.
          if (oldSize > limit && newSize > limit && newSize <= oldSize) {
            return true
          }

          // The limit has already been exceeded and will be increased further.
          if (oldSize > limit && newSize > limit && newSize > oldSize) {
            return false
          }

          const isPaste = transaction.getMeta('paste')

          // Block all exceeding transactions that were not pasted.
          if (!isPaste) {
            return false
          }

          let updatedTransaction = transaction
          if (isPaste) {
            // trim words beyond limit,
            // ! can't strip html here as we lose cursor position
            const node = transaction.doc
            const nodeSize = node.content.size
            const text = node.textBetween(0, nodeSize, ' ', ' ')
            const words = text.split(/\s+/g)
            const trimmedWordsArr = words.slice(0, limit)

            updatedTransaction = transaction
              .deleteRange(0, nodeSize)
              .insertText(trimmedWordsArr.join(' '))

            // In some situations, the limit will continue to be exceeded after trimming.
            // This happens e.g. when truncating within a complex node (e.g. table)
            // and ProseMirror has to close this node again.
            // If this is the case, we prevent the transaction completely.
            const updatedSize = this.storage.words({ node: updatedTransaction.doc })

            if (updatedSize > limit) {
              return false
            }
          }

          return true
        },
      }),
    ]
  },
})
